/**
 * `sibyl init` — the annotated `sibyl.toml`, generated from the schema rather than written beside
 * it.
 *
 * ⛔ THERE IS NO LIST OF KEYS IN THIS FILE, AND THERE MUST NEVER BE ONE. Every key, section, table
 * and array-of-tables in the output comes from walking `DEFAULTS` at runtime. A key added to
 * src/config/schema.ts appears here on the next run with no edit to this module, and a key deleted
 * there disappears from here for the same reason.
 *
 * The defect this prevents is already on the board: docs/spec/ledger.md records **34 configuration
 * keys documented with no field behind them** — `stage`, `cache`, `repo_policy`, `log`,
 * `[triage].order`, `dedupe_scope`, `stale_after` and the rest — because docs/spec/config.md's
 * default file was typed by hand next to the schema and the two drifted. A hand-maintained `init`
 * would recreate that on day one, and it would be worse, because a shipped file is read as
 * authoritative in a way a document is not.
 *
 * ⛔ EVERY LINE OF THE OUTPUT IS A COMMENT, INCLUDING THE SETTINGS. A file whose every line is
 * commented documents the defaults without overriding a single one, so writing it into a repository
 * changes nothing and rewriting it after an upgrade changes nothing either. Deleting a `# ` is the
 * whole act of configuring. Two prefixes, so the two kinds of line can be told apart mechanically:
 *
 *     `# key = value`   a setting, at its built-in default, inert until you delete the `# `
 *     `## prose`        explanation
 *
 * ⛔ EVERY WORD OF PROSE BELOW THE HEADER IS LIFTED VERBATIM FROM src/config/schema.ts. Nothing in
 * `ANNOTATIONS` is re-worded, summarised or invented, and src/cli/init.test.ts proves it by
 * normalising the schema source and asserting each string is present in it. Re-wording is how the
 * documentation and the behaviour start to disagree — which is the same defect as the 34 keys,
 * arriving one sentence at a time instead of all at once.
 *
 * ⚠️ `ANNOTATIONS` is a map of key → explanation. It is read, never iterated, and it can neither add
 * a key to the output nor remove one: an entry whose key is never emitted is reported by
 * `unusedAnnotations()` and fails the suite. That is what keeps it from becoming the hand-written
 * list the file header forbids.
 *
 * Prose and the reason behind each default: docs/spec/config.md
 */

import { DEFAULTS } from "../config/schema.js"

export class InitError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "InitError"
  }
}

// ── Layout ─────────────────────────────────────────────────────────────────────────────────────

/** Prose and the one-line array form wrap here. Wide enough for a path glob, narrow enough to read. */
const WIDTH = 96

/**
 * The only hand-written prose in the output, and the only part `init.test.ts` exempts from the
 * verbatim-from-schema check. It says how to read the file and nothing about any individual key,
 * because a claim about a key is exactly the thing that drifts.
 */
export const HEADER_LINES: readonly string[] = [
  "## sibyl.toml — local-first automated pull-request review.",
  "##",
  "## ⛔ NO SECRETS IN THIS FILE, EVER. Credentials live in the credential store and are referred",
  "##    to here by NAME only. sibyl refuses to start if a value here matches a credential-shaped",
  "##    pattern, and the offending value is never echoed back to you.",
  "##",
  "## Every `# key = value` line below is the BUILT-IN DEFAULT, commented out. This file therefore",
  "## changes nothing until you delete a `# `, which is what makes it safe to write and to rewrite.",
  "## Lines beginning `##` are explanation, lifted verbatim from the schema that defines the key.",
  "##",
  "## Generated by `sibyl init` by walking the schema. There is no list of keys in the generator;",
  "## if a key is missing from this file it is missing from the program.",
]

// ── The explanations ───────────────────────────────────────────────────────────────────────────

/**
 * Key → the schema's own words about it.
 *
 * ⛔ VERBATIM, WHITESPACE-COLLAPSED, NOT PARAPHRASED. src/cli/init.test.ts asserts every string
 * below occurs in src/config/schema.ts, so re-wording the schema breaks the build rather than
 * quietly leaving two descriptions of one field in circulation.
 *
 * Keys are dotted schema paths in the schema's own camelCase — `backend.claudeCli.tools`, not
 * `backend.claude_cli.tools` — because the path is a fact about the code, and the snake_case form is
 * a fact about TOML. Inside an array-of-tables an explanation may be pinned to one element by index
 * (`dimension.2.severityCeiling`); an unpinned one is emitted at the first element that carries the
 * key, so it is stated once rather than five times.
 */
export const ANNOTATIONS: ReadonlyMap<string, string> = new Map([
  // [server]
  ["server.bind", "⛔ Loopback only. This surface can post to a shared repo and has no authentication."],
  [
    "server.allowNonLoopback",
    "Must be flipped explicitly before `bind` may be anything else, and warns on every boot.",
  ],

  // [backend]
  [
    "backend.probeCache",
    'How long an availability probe is reused. Re-probing per dimension is five process spawns to answer one question. "process" caches for the lifetime of the run; "off" probes every time.',
  ],
  [
    "backend.onUnavailable",
    "⛔ Never silently degrade to a weaker backend and present the result as a full review.",
  ],
  [
    "backend.claudeCli.model",
    "Pinned by full id, never an alias — an alias moving would silently change stored provenance.",
  ],
  [
    "backend.claudeCli.permissionPrompts",
    "⛔ Nobody is at the keyboard; anything that would prompt is denied, not left hanging.",
  ],
  [
    "backend.claudeCli.tools",
    "⛔ The most load-bearing default in the file. A reviewer reads the branch; it does not run it.",
  ],
  [
    "backend.claudeCli.maxBudgetUsd",
    "⚠️ 0 means uncapped, which is what subscription billing implies.",
  ],
  [
    "backend.anthropicApi.thinking",
    "⛔ `budget_tokens` does not exist on this model family and 400s. Deliberately no knob for it.",
  ],
  [
    "backend.anthropicApi.promptCacheTtl",
    "The shared corpus is reused across dimensions and PRs; 1h pays back after two reads.",
  ],
  ["backend.anthropicApi.apiKeyName", "⛔ The NAME of an environment variable, never a value."],

  // [[dimension]]
  ["dimensions.description", "What the harness routes on. Written for a machine, not a person."],
  [
    "dimensions.promptAppend",
    "Keep the shipped prompt and add a house rule, rather than forking it and missing every fix.",
  ],
  [
    "dimensions.reportEverything",
    '⛔ Tells the model NOT to self-filter. Models follow "only report high-severity" literally: they find the bug, judge it below the stated bar, and drop it. Filtering belongs in triage, which is auditable and tunable; a silent drop inside the model is neither.',
  ],
  ["dimensions.weight", "⛔ Tie-break ordering only. Never summed — adding verdicts invents authority."],
  ["dimensions.2.severityCeiling", "⭐ Style must not be able to fail a build."],
  [
    "dimensions.4.severityCeiling",
    '⭐ "You didn\'t need to write this" is never a merge blocker from a machine.',
  ],

  // [severity]
  [
    "severity.actions.high",
    "⭐ A HIGH usually needs a human to choose the fix; a one-click patch on a security bug is how a wrong fix gets merged fast.",
  ],
  [
    "severity.actions.low",
    "⭐ LOW gets no comment of its own. A wall of nits is how a review gets closed unread.",
  ],
  [
    "severity.preExisting.comment",
    "⛔ A PR author is not accountable for code they did not touch.",
  ],

  // [suggestion]
  [
    "suggestion.mustBeWithinDiff",
    "⛔ GitHub silently drops a suggestion anchored outside the diff. Refusing is honest.",
  ],

  // [paths]
  ["paths.followSymlinks", "⛔ A symlink out of the clone is a path escape."],
  [
    "paths.maxFileBytes",
    "Above this a file is excluded AND named in the report, never silently skipped.",
  ],
  [
    "paths.overrides.0",
    "A bad migration is not recoverable by a revert; that asymmetry deserves a floor.",
  ],
  ["paths.overrides.severityFloor", "Promotion beats suppression when a floor and a ceiling cross."],
  [
    "paths.overrides.alwaysReview",
    "Review these even when diff-size limits would have truncated the run.",
  ],

  // [diff]
  [
    "diff.contextLines",
    "Well above git's 3: three lines reliably hides the guard clause that makes it not a bug.",
  ],
  [
    "diff.onExceed",
    "⛔ truncate/skip produce a review that LOOKS complete over a diff that was not read.",
  ],
  ["diff.ignoreWhitespace", "⚠️ Whitespace CAN be the bug — Python, YAML, heredocs, Makefiles."],

  // [concurrency]
  [
    "concurrency.dimensions",
    "⭐ Not 5: five simultaneous processes is the wrong shape on a laptop also running a dev server.",
  ],

  // [triage]
  [
    "triage.requireHuman",
    "⛔ THE MASTER SWITCH. While true, nothing posts without a person, whatever else says.",
  ],
  ["triage.carryForward", "A dismissal survives a force-push if the cited code survives it."],

  // [comments]
  ["comments.maxPerReview", "Past ~25 inline comments a review is read as noise and closed."],
  [
    "comments.includeConfidence",
    "⚠️ A displayed number reads as a measurement. It is a self-report. Kept for triage only.",
  ],
  ["comments.summaryOnEmpty", '"Reviewed, found nothing" is a RESULT. Silence looks like a crash.'],
  ["comments.mentionUsers", "⛔ @mentions re-fire on every edit."],

  // [post] — the three locks. The third is triage.require_human, above.
  ["post.enabled", "LOCK 1"],
  ["post.dryRun", "LOCK 2"],
  [
    "post.reviewEvent",
    "⛔ A machine does not approve. Also: our token is the PR author, who cannot approve.",
  ],
  ["post.onConflict", "If the head sha moved between review and post, abort."],
  [
    "post.commitStatus.targetUrlBase",
    "⚠️ Empty on purpose — a 127.0.0.1 link in a shared PR is dead for everyone but you.",
  ],
  [
    "post.commitStatus.onError",
    "⛔ A broken reviewer must not fail someone's build. Our outage is not their defect.",
  ],

  // [repo]
  [
    "repo.pruneAfter",
    'How long a clone may sit idle before it is removed. ⭐ `"0"` disables pruning: the safe reading of "no window configured" is delete nothing, never delete everything. ⚠️ Documented in docs/spec/config.md since W1 and absent from this file until W3 needed it — a user copying the shipped default file got a key the loader knew nothing about.',
  ],
  [
    "repo.allowInPlaceReview",
    "⛔ Reviewing the operator's live tree. Exists only because everything is configurable.",
  ],
  [
    "repo.neverPruneWorktrees",
    "⛔ Never `git worktree prune` — it deregisters other sessions' worktrees.",
  ],

  // [secrets]
  ["secrets.readback", "⛔ Not configurable to anything else. Any other value is a startup error."],
])

/**
 * ⛔ The one place a schema name and its TOML name disagree, and it is not cosmetic.
 * src/config/load.ts reads `data["dimension"]` — singular — and `KNOWN_TOP_LEVEL` does not contain
 * `dimensions`, so emitting the schema's own field name would produce a file the loader rejects as
 * an unknown top-level key. One rename, stated out loud, is cheaper than a file that fails to load.
 */
const TOML_TABLE_NAME: ReadonlyMap<string, string> = new Map([["dimensions", "dimension"]])

// ── TOML, serialised ───────────────────────────────────────────────────────────────────────────

type Plain = { readonly [key: string]: unknown }

function isPlain(v: unknown): v is Plain {
  return typeof v === "object" && v !== null && !Array.isArray(v)
}

/** An array every element of which is a table — `[[section.item]]` rather than an inline array. */
function isTableArray(v: unknown): v is readonly Plain[] {
  return Array.isArray(v) && v.length > 0 && v.every(isPlain)
}

/** The schema's camelCase to TOML's snake_case. Exactly inverted by the loader's own `camel()`. */
function tomlKey(key: string): string {
  return key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)
}

/**
 * A TOML basic string.
 *
 * ⛔ No raw C0 control character ever reaches the file. A literal NUL or ESC in a config makes
 * `file` report it as data, makes `grep` return no hit for a key that is plainly there, and makes
 * `git diff` print `Bin` instead of the change — the escape is the identical byte on read and stays
 * greppable on disk.
 */
function tomlString(s: string): string {
  let out = '"'
  for (const ch of s) {
    const code = ch.codePointAt(0) ?? 0
    if (ch === '"') out += '\\"'
    else if (ch === "\\") out += "\\\\"
    else if (ch === "\n") out += "\\n"
    else if (ch === "\r") out += "\\r"
    else if (ch === "\t") out += "\\t"
    else if (code < 0x20 || code === 0x7f) out += `\\u${code.toString(16).padStart(4, "0")}`
    else out += ch
  }
  return `${out}"`
}

/**
 * One scalar.
 *
 * ⛔ The `default` arm throws rather than returning something. An exhaustive switch over a runtime
 * value has no floor of its own: an unhandled type would fall through, yield `undefined`, and
 * surface as a TOML parse error in a test three files away that names none of this.
 */
function tomlScalar(value: unknown, where: string): string {
  switch (typeof value) {
    case "string":
      return tomlString(value)
    case "boolean":
      return value ? "true" : "false"
    case "number":
      if (!Number.isFinite(value)) {
        throw new InitError(`${where}: ${String(value)} has no TOML form`)
      }
      return String(value)
    default:
      throw new InitError(`${where}: a ${typeof value} cannot be written to TOML`)
  }
}

/** An array of scalars, on one line when it fits and one element per line when it does not. */
function tomlArray(value: readonly unknown[], where: string, indentWidth: number): string[] {
  if (value.length === 0) return ["[]"]
  const parts = value.map((element, i) => tomlScalar(element, `${where}[${i}]`))
  const oneLine = `[${parts.join(", ")}]`
  if (indentWidth + oneLine.length <= WIDTH) return [oneLine]
  return ["[", ...parts.map((p) => `  ${p},`), "]"]
}

// ── Emitting ───────────────────────────────────────────────────────────────────────────────────

interface Sink {
  readonly lines: string[]
  /** Which annotations were attached to a key that really exists. See `unusedAnnotations`. */
  readonly used: Set<string>
}

/** Wrap prose onto `## ` lines. A word wider than the column goes on a line of its own, unbroken. */
function wrapProse(text: string): string[] {
  const out: string[] = []
  let line = ""
  for (const word of text.split(" ")) {
    if (line === "") line = word
    else if (`## ${line} ${word}`.length <= WIDTH) line += ` ${word}`
    else {
      out.push(`## ${line}`)
      line = word
    }
  }
  if (line !== "") out.push(`## ${line}`)
  return out
}

/** Attach the schema's words for `key`, if it has any. Records the hit so a stale entry is caught. */
function annotate(sink: Sink, ...candidates: readonly string[]): void {
  for (const key of candidates) {
    const text = ANNOTATIONS.get(key)
    if (text === undefined) continue
    sink.used.add(key)
    sink.lines.push(...wrapProse(text))
    return
  }
}

function banner(sink: Sink, name: string): void {
  const head = `## ── [${name}] `
  sink.lines.push("")
  sink.lines.push(head + "─".repeat(Math.max(3, WIDTH - head.length)))
}

/** `# key = value`, with the schema's explanation above it. */
function emitKey(sink: Sink, key: string, value: unknown, where: string, lookups: readonly string[]): void {
  annotate(sink, ...lookups)
  const name = tomlKey(key)
  if (Array.isArray(value)) {
    const [first, ...rest] = tomlArray(value, where, name.length + 5)
    sink.lines.push(`# ${name} = ${first ?? "[]"}`)
    for (const line of rest) sink.lines.push(`# ${line}`)
    return
  }
  sink.lines.push(`# ${name} = ${tomlScalar(value, where)}`)
}

function partition(table: Plain): {
  scalars: [string, unknown][]
  tables: [string, Plain][]
  tableArrays: [string, readonly Plain[]][]
} {
  const scalars: [string, unknown][] = []
  const tables: [string, Plain][] = []
  const tableArrays: [string, readonly Plain[]][] = []
  for (const [key, value] of Object.entries(table)) {
    if (isTableArray(value)) tableArrays.push([key, value])
    else if (isPlain(value)) tables.push([key, value])
    else scalars.push([key, value])
  }
  return { scalars, tables, tableArrays }
}

/**
 * One `[table]` and everything under it.
 *
 * ⚠️ Scalars are written before any sub-table, because TOML binds a bare key to the most recently
 * opened table. Emitting `[server]`, then `[server.theme]`, then `port` would put `port` inside the
 * theme — a file that parses cleanly and means something else.
 */
function emitTable(sink: Sink, table: Plain, schemaPath: string, tomlPath: string): void {
  const { scalars, tables, tableArrays } = partition(table)
  annotate(sink, schemaPath)
  sink.lines.push(`# [${tomlPath}]`)
  for (const [key, value] of scalars) {
    emitKey(sink, key, value, `${schemaPath}.${key}`, [`${schemaPath}.${key}`])
  }
  for (const [key, value] of tables) {
    sink.lines.push("")
    emitTable(sink, value, `${schemaPath}.${key}`, `${tomlPath}.${tomlKey(key)}`)
  }
  for (const [key, value] of tableArrays) {
    emitTableArray(sink, value, `${schemaPath}.${key}`, `${tomlPath}.${tomlKey(key)}`)
  }
}

/**
 * `[[section.item]]`, once per element.
 *
 * ⭐ An explanation that is not pinned to an index is emitted at the FIRST element that carries the
 * key, not at every one. Five identical ⛔ paragraphs down five dimension blocks train the reader to
 * skip the block, which is the opposite of what the paragraph is for.
 */
function emitTableArray(
  sink: Sink,
  elements: readonly Plain[],
  schemaPath: string,
  tomlPath: string,
): void {
  const explained = new Set<string>()
  elements.forEach((element, index) => {
    const { scalars, tables, tableArrays } = partition(element)
    if (tables.length > 0 || tableArrays.length > 0) {
      // ⛔ Refuses rather than dropping the nested table. A key silently absent from this file is a
      // setting the reader believes does not exist — the same defect as a key documented with no
      // field, arriving from the other direction.
      throw new InitError(
        `${schemaPath}[${index}]: a nested table inside an array-of-tables is not serialised yet`,
      )
    }
    sink.lines.push("")
    annotate(sink, `${schemaPath}.${index}`)
    sink.lines.push(`# [[${tomlPath}]]`)
    for (const [key, value] of scalars) {
      const pinned = `${schemaPath}.${index}.${key}`
      const generic = `${schemaPath}.${key}`
      const lookups = explained.has(generic) ? [pinned] : [pinned, generic]
      explained.add(generic)
      emitKey(sink, key, value, `${schemaPath}[${index}].${key}`, lookups)
    }
  })
}

function render(): Sink {
  const sink: Sink = { lines: [...HEADER_LINES], used: new Set() }
  const root = DEFAULTS as unknown as Plain
  const { scalars, tables, tableArrays } = partition(root)

  sink.lines.push("")
  for (const [key, value] of scalars) {
    emitKey(sink, key, value, key, [key])
  }

  // ⚠️ Sections keep the order they have in the schema literal. That order is the reading order the
  // schema's author chose, and reproducing it is what lets the two files be read side by side.
  for (const [key, value] of [...tables, ...tableArrays].sort(
    (a, b) => Object.keys(root).indexOf(a[0]) - Object.keys(root).indexOf(b[0]),
  )) {
    const name = TOML_TABLE_NAME.get(key) ?? tomlKey(key)
    banner(sink, name)
    if (isTableArray(value)) emitTableArray(sink, value, key, name)
    else if (isPlain(value)) emitTable(sink, value, key, name)
  }

  sink.lines.push("")
  return sink
}

/**
 * The whole annotated file.
 *
 * Deterministic: called twice in one process it returns the identical string, because everything it
 * reads is `DEFAULTS` and `DEFAULTS` is a constant. `sibyl init` can therefore be run over an
 * existing file and the diff is empty unless the schema moved.
 */
export function renderAnnotatedToml(): string {
  return `${render().lines.join("\n")}\n`
}

/**
 * Explanations that matched no emitted key.
 *
 * ⛔ This is the guard that stops `ANNOTATIONS` becoming the hand-written key list the file header
 * forbids. An entry left behind after a field is renamed or deleted is a sentence about something
 * that no longer exists, which is exactly the shape of the 34-key defect; the suite fails on it
 * rather than shipping it.
 */
export function unusedAnnotations(): readonly string[] {
  const { used } = render()
  return [...ANNOTATIONS.keys()].filter((key) => !used.has(key)).sort()
}

/**
 * The file as the loader would see it: prose removed, one `# ` peeled off every setting.
 *
 * ⭐ Exported because it is the round trip, and the round trip is the only thing that proves the
 * generator is real rather than a plausible-looking string. src/cli/init.test.ts parses this and
 * asserts it equals `DEFAULTS` field for field.
 */
export function uncomment(toml: string): string {
  return toml
    .split("\n")
    .filter((line) => !line.startsWith("##"))
    .map((line) => (line.startsWith("# ") ? line.slice(2) : line))
    .join("\n")
}
